// =============================================================================
// MeuDriver — Adapter de geolocalização
// -----------------------------------------------------------------------------
// Abstrai a origem da posição para que os hooks não precisem saber se rodam no
// navegador ou dentro do app nativo:
//
//   * Nativo (Capacitor)  → @capacitor/geolocation, carregado sob demanda.
//   * Navegador / PWA     → navigator.geolocation.
//
// ⚠️ LIMITAÇÃO ESTRUTURAL DO PWA
// No navegador, `watchPosition` para quando a aba perde o foco — no iOS Safari,
// quase imediatamente. Um instrutor dirigindo com o celular bloqueado NÃO é
// rastreado no PWA. Rastreio em segundo plano exige o app nativo com permissão
// de "localização o tempo todo" (ACOES-MANUAIS item 24). Use `isBackgroundCapable()`
// para a UI avisar o instrutor em vez de prometer o que não entrega.
// =============================================================================

export interface Position {
  lat: number
  lng: number
  /** Precisão horizontal em metros, quando o dispositivo informa. */
  accuracyM: number | null
  /** Direção em graus (0 = norte), quando disponível. Gira o ícone do carro. */
  headingDeg: number | null
  /** Velocidade em m/s, quando disponível. */
  speedMs: number | null
  timestamp: number
}

export type GeolocationErrorCode =
  | 'permission_denied'
  | 'unavailable'
  | 'timeout'
  | 'unsupported'

export class GeolocationError extends Error {
  code: GeolocationErrorCode
  constructor(code: GeolocationErrorCode, message: string) {
    super(message)
    this.name = 'GeolocationError'
    this.code = code
  }
}

// ─── Detecção de ambiente ────────────────────────────────────────────────────

interface CapacitorGlobal {
  isNativePlatform?: () => boolean
}

function capacitor(): CapacitorGlobal | null {
  const c = (globalThis as { Capacitor?: CapacitorGlobal }).Capacitor
  return c ?? null
}

/** `true` quando rodando dentro do shell nativo (Capacitor), não no navegador. */
export function isNative(): boolean {
  return capacitor()?.isNativePlatform?.() === true
}

/**
 * `true` quando a posição continua chegando com o app em segundo plano.
 *
 * Hoje só o nativo. Mesmo lá, depende de o usuário ter concedido a permissão
 * de "sempre" — por isso a UI deve tratar isto como "pode funcionar", não como
 * garantia.
 */
export function isBackgroundCapable(): boolean {
  return isNative()
}

// ─── Normalização ────────────────────────────────────────────────────────────

interface RawCoords {
  latitude: number
  longitude: number
  accuracy?: number | null
  heading?: number | null
  speed?: number | null
}

function toPosition(coords: RawCoords, timestamp: number): Position {
  const num = (v: number | null | undefined): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null
  return {
    lat: coords.latitude,
    lng: coords.longitude,
    accuracyM: num(coords.accuracy),
    headingDeg: num(coords.heading),
    speedMs: num(coords.speed),
    timestamp,
  }
}

function mapBrowserError(err: GeolocationPositionError): GeolocationError {
  if (err.code === err.PERMISSION_DENIED) {
    return new GeolocationError('permission_denied', 'Permissão de localização negada.')
  }
  if (err.code === err.TIMEOUT) {
    return new GeolocationError('timeout', 'Tempo esgotado ao obter a localização.')
  }
  return new GeolocationError('unavailable', 'Localização indisponível no momento.')
}

// ─── Capacitor (carregado sob demanda) ───────────────────────────────────────

interface CapacitorPosition {
  coords: RawCoords
  timestamp: number
}

interface CapacitorGeolocationPlugin {
  requestPermissions: () => Promise<{ location: string }>
  watchPosition: (
    opts: { enableHighAccuracy: boolean; timeout: number },
    cb: (pos: CapacitorPosition | null, err?: unknown) => void,
  ) => Promise<string>
  clearWatch: (opts: { id: string }) => Promise<void>
}

async function loadCapacitorGeolocation(): Promise<CapacitorGeolocationPlugin | null> {
  try {
    // Import dinâmico e por variável: no build web o pacote pode não estar
    // presente, e não queremos que o bundler tente resolvê-lo estaticamente.
    const mod = (await import(/* @vite-ignore */ '@capacitor/geolocation')) as {
      Geolocation?: CapacitorGeolocationPlugin
    }
    return mod.Geolocation ?? null
  } catch {
    return null
  }
}

// ─── API pública ─────────────────────────────────────────────────────────────

export interface WatchHandle {
  stop: () => void
}

/**
 * Observa a posição continuamente até `stop()`.
 *
 * `onError` recebe erros recuperáveis (um GPS que falhou uma leitura não deve
 * derrubar o rastreio inteiro) — cabe ao chamador decidir quando desistir.
 */
export async function watchPosition(
  onPosition: (pos: Position) => void,
  onError: (err: GeolocationError) => void,
): Promise<WatchHandle> {
  if (isNative()) {
    const plugin = await loadCapacitorGeolocation()
    if (plugin) {
      const perm = await plugin.requestPermissions().catch(() => null)
      if (perm && perm.location === 'denied') {
        throw new GeolocationError('permission_denied', 'Permissão de localização negada.')
      }
      const id = await plugin.watchPosition(
        { enableHighAccuracy: true, timeout: 15_000 },
        (pos, err) => {
          if (err || !pos) {
            onError(new GeolocationError('unavailable', 'Falha ao ler a localização.'))
            return
          }
          onPosition(toPosition(pos.coords, pos.timestamp))
        },
      )
      return { stop: () => void plugin.clearWatch({ id }) }
    }
    // Sem o plugin, cai para o navegador embutido no WebView.
  }

  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    throw new GeolocationError('unsupported', 'Este dispositivo não suporta geolocalização.')
  }

  const id = navigator.geolocation.watchPosition(
    (pos) => onPosition(toPosition(pos.coords, pos.timestamp)),
    (err) => onError(mapBrowserError(err)),
    { enableHighAccuracy: true, timeout: 15_000, maximumAge: 5_000 },
  )
  return { stop: () => navigator.geolocation.clearWatch(id) }
}

/** Leitura única — usada para carimbar os marcos (saiu, chegou). */
export function getCurrentPosition(): Promise<Position> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new GeolocationError('unsupported', 'Este dispositivo não suporta geolocalização.'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(toPosition(pos.coords, pos.timestamp)),
      (err) => reject(mapBrowserError(err)),
      { enableHighAccuracy: true, timeout: 15_000 },
    )
  })
}
